import { ApiJsonClientError } from "@/lib/query/core/api-json-client";

// Backend error codes this module can surface. Structured here so callers
// don't stringly-compare `error.message` (which may be a raw/internal
// technical string) and so new codes are grepable in one place.
export type BostaErrorCode =
  | "BOSTA_DISABLED"
  | "BOSTA_WEBHOOK_KEY_MISSING"
  | "BOSTA_NOT_CONNECTED"
  | "BOSTA_API_KEY_INVALID"
  | "BOSTA_API_UNAVAILABLE"
  | "BOSTA_UNREACHABLE"
  | "BOSTA_UNAVAILABLE"
  | "BOSTA_DELIVERY_REJECTED"
  | "BOSTA_CANCEL_REJECTED"
  | "BOSTA_CITY_NOT_FOUND"
  | "BOSTA_DISTRICT_NOT_FOUND"
  | "BOSTA_DISTRICT_CITY_MISMATCH"
  | "BOSTA_WEBHOOK_MISSING_ID"
  | "BOSTA_WEBHOOK_MISSING_STATE"
  | "BOSTA_INTEGRATION_NOT_FOUND"
  | "ORDER_NOT_DISPATCHABLE"
  | "ORDER_ALREADY_SHIPPED"
  | "SHIPMENT_NOT_FOUND"
  | "NO_ORDERS_SELECTED"
  | "NO_SHIPMENTS_FOUND"
  | "ORDER_NOT_FOUND";

// Short, user-facing fallbacks so callers never show the raw backend
// message to the seller. Real localization belongs in portal/messages/*.json,
// but rendering the message verbatim is the minimum required fix — the
// previous pattern `error.message` leaked internal phrasing.
const DEFAULT_MESSAGES: Readonly<Record<BostaErrorCode, string>> = {
  BOSTA_DISABLED:
    "Bosta integration is currently disabled on this platform. Contact support.",
  BOSTA_WEBHOOK_KEY_MISSING:
    "This Bosta connection is missing its webhook key. Disconnect and reconnect Bosta in Settings.",
  BOSTA_NOT_CONNECTED:
    "Bosta is not connected for this store. Connect Bosta in Settings → Integrations first.",
  BOSTA_API_KEY_INVALID:
    "Bosta rejected this API key. Make sure you copied a Full-Access key from Bosta’s dashboard.",
  BOSTA_API_UNAVAILABLE:
    "Bosta couldn’t confirm your API key right now. Please try again in a moment.",
  BOSTA_UNREACHABLE: "Could not reach Bosta. Please try again.",
  BOSTA_UNAVAILABLE: "Bosta is not responding correctly. Please try again.",
  BOSTA_DELIVERY_REJECTED:
    "Bosta rejected this delivery. Double-check the pickup, city, and district.",
  BOSTA_CANCEL_REJECTED:
    "Bosta refused to cancel — the delivery may already be in transit.",
  BOSTA_CITY_NOT_FOUND:
    "Selected city isn’t recognised by Bosta anymore. Refresh the page and try again.",
  BOSTA_DISTRICT_NOT_FOUND:
    "Selected district isn’t recognised by Bosta anymore. Refresh the page and try again.",
  BOSTA_DISTRICT_CITY_MISMATCH:
    "Selected district doesn’t belong to the selected city.",
  BOSTA_WEBHOOK_MISSING_ID: "Bosta webhook payload is missing a delivery id.",
  BOSTA_WEBHOOK_MISSING_STATE:
    "Bosta webhook payload is missing a numeric state.",
  BOSTA_INTEGRATION_NOT_FOUND: "Bosta integration not found for this store.",
  ORDER_NOT_DISPATCHABLE:
    "Order must be confirmed before it can be dispatched.",
  ORDER_ALREADY_SHIPPED: "This order has already been dispatched.",
  SHIPMENT_NOT_FOUND: "No shipment exists for this order yet.",
  NO_ORDERS_SELECTED: "Select at least one order before printing AWBs.",
  NO_SHIPMENTS_FOUND:
    "None of the selected orders have a shipment yet. Dispatch them first.",
  ORDER_NOT_FOUND: "Order not found.",
};

// Extract a user-safe message from a mutation/query error. Prefers the
// structured backend code, falls back to a short generic message. Never
// returns undefined so callers can always render the result directly.
export function resolveBostaErrorMessage(
  error: unknown,
  fallback = "Something went wrong. Please try again.",
): { code: BostaErrorCode | null; message: string } {
  if (error instanceof ApiJsonClientError && error.code) {
    const code = error.code as BostaErrorCode;
    const mapped = DEFAULT_MESSAGES[code];
    if (mapped) return { code, message: mapped };
    // Unknown code → don't leak backend `.message` to the user.
    return { code, message: fallback };
  }
  return { code: null, message: fallback };
}
